import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import LadowanieStrony from "./common/Ladowanie/LadowanieStrony";
import AccessDeniedScreen from "./AccessDeniedScreen/AccessDeniedScreen";
import { pathStartsWith } from "../utils/routeHelpers";
import { useTelefon } from "../utils/urzadzenieMobilne";

// Brak uprawnień → PEŁNOEKRANOWY ekran „brak uprawnień" (nie ciche przekierowanie na pulpit).
// Moduł nie startuje: nie montuje się żaden komponent strony i nie leci żadne zapytanie o dane.
// permissionKey — klucz PERM, którego zabrakło: ekran wypełnia nim prośbę o dostęp (szuflada do IT).
// [S-6, 2026-09-09] `mozliwyWniosek` MUSI przejść przez tę osłonkę: gałąź „moduł wyłączony globalnie” podawała je od 2026-09-06,
// ale osłonka miała zamkniętą listę propsów i ekran odmowy dalej proponował wniosek do IT o rzecz, której IT nie ma jak spełnić.
const AccessDenied = ({ title, message, moduleLabel, reason, permissionKey, mozliwyWniosek }) => (
    <AccessDeniedScreen title={title} message={message} moduleLabel={moduleLabel} reason={reason} permissionKey={permissionKey}
        mozliwyWniosek={mozliwyWniosek} />
);

const ProtectedRoute = ({ requiredPermission, requiredType, restrictedModule, requireO365, moduleLabel, children }) => {
  const { user, loading, hasPermission, isModuleRestricted, isModuleGloballyHidden, isModuleContractGated } = useAuth();
  const location = useLocation();
  const telefon = useTelefon();

  // [S19 2026-09-14] Sprawdzanie sesji: stan ładowania na cały widok (kropki, pasek, czas) zamiast rozmytej
  // nakładki. To WŁASNY wskaźnik trasy — globalny GlobalLoader czyta LoadingContext i przy sprawdzaniu sesji milczy.
  if (loading) {
    return <LadowanieStrony wariant="ekran" etykieta="Sprawdzanie sesji…" />;
  }

  if (!user) {
    return <Navigate to={localizedPath("login")} replace />;
  }

  /* [HASLA-LOKALNE, decyzja właściciela 2026-09-27] Hasło tymczasowe od administratora albo po terminie ważności (45 dni):
     z KAŻDEGO chronionego adresu prowadzimy wyłącznie do ekranu zmiany hasła — serwer i tak odrzuca resztę wywołań
     kodem 403 „haslo_do_zmiany”. Flaga przychodzi z logowania i z /users/me (UserInfo.hasloDoZmiany). */
  if (user.hasloDoZmiany === true) {
    return <Navigate to={localizedPath("zmianaHasla")} replace />;
  }

  /* [zgłoszenie właściciela 2026-09-16] „Zablokuj wejście do sekcji admin z urządzeń mobilnych,
     urządzenie mobilne ma być dla usera do podstawowych prac (…) mój dział ma być."

     Blokada stoi TUTAJ, a nie przy poszczególnych trasach panelu, bo tras panelu jest kilkanaście
     i dochodzą nowe — warunek przy każdej z nich byłby listą do zapomnienia przy pierwszej nowej
     zakładce. Jedno miejsce zamyka /admin i wszystko pod nim, także zakładkę z pamięci przeglądarki
     i link z powiadomienia.

     To NIE jest brak uprawnień, więc ekran nie proponuje wniosku do IT: ta sama osoba wejdzie tu
     z komputera bez żadnego nadania. Reszta aplikacji (pulpit, zgłoszenia, Mój Dział) zostaje. */
  if (telefon && pathStartsWith(location.pathname, "admin")) {
    return (
      <AccessDenied
        title="Panel administracyjny tylko na komputerze"
        message="Administracja aplikacji jest wyłączona na telefonie. Ta sama sekcja otworzy się na komputerze — bez żadnych dodatkowych nadań."
        moduleLabel={moduleLabel}
        reason="Decyzja właściciela aplikacji: telefon służy do bieżącej pracy (pulpit, zgłoszenia, Mój Dział), a nie do konfiguracji systemu."
        mozliwyWniosek={false}
      />
    );
  }

  if (requiredType != null && user.type !== requiredType) {
    return <AccessDenied moduleLabel={moduleLabel} reason="Ta sekcja wymaga innego typu konta." />;
  }

  // Wymaga konta Office 365 (user.domain = bool isO365). Konta lokalne nie mają tej zakładki.
  if (requireO365 && user.domain !== true) {
    return (
      <AccessDenied
        message="Ta zakładka jest dostępna wyłącznie dla kont Office 365."
        moduleLabel={moduleLabel}
        reason="Twoje konto nie jest kontem Office 365."
      />
    );
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    const keys = Array.isArray(requiredPermission) ? requiredPermission.join(" lub ") : requiredPermission;
    return (
      <AccessDenied
        moduleLabel={moduleLabel}
        permissionKey={keys}
        reason={`Wymagane uprawnienie: ${keys}.`}
      />
    );
  }

  // [S-6, polecenie właściciela 2026-09-09] Zakładka poza formą zatrudnienia (Moja ścieżka kariery / Moje cele) — to nie
  // „ukrycie przez administratora”, tylko decyzja kadr w konfiguratorze; ekran mówi, kto o tym decyduje i że nie ma czego
  // nadawać (wniosek do IT nic tu nie zmieni). Sprawdzane PRZED ogólnym ukryciem, bo isModuleRestricted obejmuje oba.
  if (restrictedModule && typeof isModuleContractGated === "function" && isModuleContractGated(restrictedModule)) {
    return (
      <AccessDenied
        title="Zakładka poza Twoją formą zatrudnienia"
        message="Ta zakładka nie jest prowadzona dla Twojej formy zatrudnienia."
        moduleLabel={moduleLabel}
        reason="O tym, które formy zatrudnienia mają tę zakładkę, decyduje dział kadr (Konfigurator HR → Warianty umów)."
        mozliwyWniosek={false}
      />
    );
  }

  // Moduł menu niedostępny → brak wejścia nawet z linku:
  // (a) ukryty dla tego użytkownika (deny-list per user) LUB (b) wyłączony globalnie (ModuleVisibility).
  if (restrictedModule && isModuleRestricted(restrictedModule)) {
    return (
      <AccessDenied
        title="Moduł ukryty"
        message="Ten moduł został ukryty dla Twojego konta."
        moduleLabel={moduleLabel}
        reason="Widoczność modułu wyłączona indywidualnie dla tego użytkownika."
      />
    );
  }
  if (restrictedModule && isModuleGloballyHidden(restrictedModule)) {
    return (
      <AccessDenied
        title="Moduł wyłączony"
        message="Ten moduł jest obecnie wyłączony w całej aplikacji."
        moduleLabel={moduleLabel}
        reason="Globalna widoczność modułu wyłączona przez administratora."
        /* [polecenie właściciela 2026-09-06, pkt 1] Tu NIE MA czego nadać — nie brakuje uprawnienia,
           tylko administrator zamknął moduł wszystkim. Zgłoszenie do IT byłoby prosbą o rzecz,
           której IT nie ma jak spelnic. */
        mozliwyWniosek={false}
      />
    );
  }

  return children ? children : <Outlet />;
};

export default ProtectedRoute;
